import React, { useEffect } from 'react';
import LanguageToggle from './LanguageToggle';
import ThemeToggle from './ThemeToggle';
import { useLanguage } from '../i18n/LanguageContext';
import '../PublicAuthTheme.css';

/**
 * ALIEN GREEN–style public auth shell: warm gradient, sticky nav, centered card.
 */
export default function PublicAuthShell({
  children,
  showNav = true,
  embedded = false,
  authTheme = false,
  centerVertically = false,
  hideScrollProgress = false,
  active = 'login',
  onHome,
  onLogin,
  onRegister,
}) {
  const { t } = useLanguage();

  useEffect(() => {
    document.body.classList.add('public-theme');
    if (authTheme) {
      document.body.classList.add('public-theme--login');
    }
    return () => {
      document.body.classList.remove('public-theme', 'public-theme--login');
    };
  }, [authTheme]);

  useEffect(() => {
    const bar = document.getElementById('public-scroll-progress');
    const onScroll = () => {
      if (!bar) return;
      const el = document.documentElement;
      const scrolled = el.scrollTop || document.body.scrollTop;
      const height = el.scrollHeight - el.clientHeight;
      const pct = height > 0 ? (scrolled / height) * 100 : 0;
      bar.style.width = `${Math.max(0, Math.min(100, pct))}%`;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className={`public-shell${embedded ? ' public-shell--embedded' : ''}`}>
      {!hideScrollProgress ? (
        <div id="public-scroll-progress" className="public-scroll-progress" aria-hidden />
      ) : null}

      {showNav && !embedded ? (
        <header className="public-nav">
          <div className="public-nav__inner">
            <button type="button" className="public-nav__brand" onClick={onHome}>
              <img
                src="/branding/cb-app-logo-circle.svg"
                alt=""
                className="public-nav__brand-logo"
              />
              <span>
                cbonline{' '}
                <span className="public-nav__brand-sub">app</span>
              </span>
            </button>
            <div className="public-nav__toggles">
              <LanguageToggle variant="public" />
              <ThemeToggle variant="public" />
            </div>
            <nav className="public-nav__links" aria-label="Public navigation">
              <button type="button" className="public-nav__link" onClick={onHome}>
                {t('public.nav.home')}
              </button>
              {active !== 'login' ? (
                <button type="button" className="public-nav__link" onClick={onLogin}>
                  {t('public.nav.signIn')}
                </button>
              ) : (
                <span className="public-nav__link public-nav__link--current" aria-current="page">
                  {t('public.nav.signIn')}
                </span>
              )}
              {active !== 'register' ? (
                <button
                  type="button"
                  className="public-nav__link public-nav__link--primary"
                  onClick={onRegister}
                >
                  {t('public.nav.register')}
                </button>
              ) : (
                <span
                  className="public-nav__link public-nav__link--primary public-nav__link--current"
                  aria-current="page"
                >
                  {t('public.nav.register')}
                </span>
              )}
            </nav>
            <div className="public-nav__actions" aria-label="Quick actions">
              {active !== 'login' ? (
                <button type="button" className="public-nav__link" onClick={onLogin}>
                  {t('public.nav.signIn')}
                </button>
              ) : null}
              {active !== 'register' ? (
                <button
                  type="button"
                  className="public-nav__link public-nav__link--primary"
                  onClick={onRegister}
                >
                  {t('public.nav.register')}
                </button>
              ) : null}
            </div>
          </div>
        </header>
      ) : null}

      <main
        className={`public-main public-main--auth${
          centerVertically ? ' public-main--auth-short' : ''
        }`}
      >
        <div className="public-auth-center">{children}</div>
      </main>
    </div>
  );
}
